/**
 * @file Component wrapping FlatPickr for single date picking.
 */
import { faReply } from '@fortawesome/free-solid-svg-icons/faReply';
import confirmDatePlugin from 'flatpickr/dist/plugins/confirmDate/confirmDate';
import * as moment from 'moment';
import { List, Maybe } from 'purify-ts';
// @ts-expect-error Not yet typed.
import FlatPickr from 'vue-flatpickr-component';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { datetime, expr } from '@codegrade/utils';
import { FontAwesomeIcon } from '@/components/Icon';
import { getUniqueId, ifJustOrEmpty } from '@/utils';
import { formatMaxDate, formatMinDate } from './common';
import 'flatpickr/dist/flatpickr.css';
import 'flatpickr/dist/plugins/confirmDate/confirmDate.css';
import './DatePicker.less';
export default tsx.componentFactoryOf().create({
    name: 'date-time-picker',
    props: {
        value: p.ofType().required,
        config: p.ofType().default(() => ({})),
        noYear: p(Boolean).default(false),
        disabled: p(Boolean).default(false),
        placeholder: p(String).optional,
        popover: p(String).default(''),
        id: p(String).optional,
        reset: p.ofType().optional,
        secondsBehavior: p.ofType().required,
    },
    computed: {
        /** @returns A unique id for the DatePicker. */
        uniqueId() {
            return getUniqueId();
        },
        /** @returns A default unique id for the component or the one passed via prop. */
        computedId() {
            if (this.id == null) {
                return `date-time-picker-${this.uniqueId}`;
            }
            return this.id;
        },
    },
    methods: {
        // The nodes are not within computed properties to force re-renders on changed values.
        /**
         * Computes the slots to be passed to `b-input-group` component if present,
         * otherwise returns an empty object.
         * This is necessary because if the key is present even with no value an empty node
         * is rendered in the prepend, causing styling issues.
         *
         * @returns An object containing the slots to be passed to the subcomponent.
         */
        getBodyScopedSlots() {
            const prepend = this.$scopedSlots.prepend;
            if (prepend != null) {
                return { prepend };
            }
            else {
                return {};
            }
        },
        /**
         * Computes the Reset button node according to the prop passed to the component.
         *
         * @param reset The non-null version of the reset prop.
         * @returns The node rendering the Reset button or an empty node.
         */
        renderResetButton(reset) {
            return (<b-input-group-append v-b-popover_top_hover_focus={this.disabled ? '' : reset.popoverText}>
                    <b-button id={`${this.computedId}-reset`} key={`${this.computedId}-reset-disabled-${reset.disabled}`} onClick={() => this.makeEventEmitter(true)([])} disabled={this.disabled || reset.disabled} variant="warning">
                        <FontAwesomeIcon icon={faReply}/>
                    </b-button>
                </b-input-group-append>);
        },
        /**
         * Computes the node containing the FlatPickr component.
         *
         * @returns The node rendering the FlatPickr component.
         */
        renderBodyNode() {
            return (<FlatPickr id={this.computedId} key={this.computedId} class={['form-control datetime-picker', { 'no-year': this.noYear }]} value={this.getInternalValue().extract()} config={this.getInternalConfig()} disabled={this.disabled} placeholder={this.placeholder}/>);
        },
        /**
         * Formats the date passed as a prop to a string value to be passed to FlatPickr.
         *
         * @returns A Maybe containing the correctly formatted date from the `value` prop.
         */
        getInternalValue() {
            return this.value
                .filter(value => value.isValid())
                .map(value => datetime.formatDate(value));
        },
        /**
         * Integrates the default FlatPickr configuration with possible overrides passed to the
         * component in the `config` prop. For this component the FlatPickr mode is forcibly
         * set to single.
         *
         * @returns The configuration object to be passed to FlatPickr.
         */
        getInternalConfig() {
            const { minDate, maxDate, ...config } = this.config;
            return {
                enableTime: true,
                plugins: [
                    confirmDatePlugin({
                        showAlways: true,
                        confirmIcon: '',
                        confirmText: 'Close',
                    }),
                ],
                minuteIncrement: 1,
                time_24hr: true,
                onChange: this.makeEventEmitter(),
                defaultHour: 23,
                defaultMinute: 59,
                static: this.noYear,
                ...config,
                minDate: formatMinDate(minDate),
                maxDate: formatMaxDate(maxDate),
                // forces this component to behave in single mode. Other modes are encoded in other components.
                mode: 'single',
            };
        },
        /**
         * Returns an event emitter callback.
         *
         * @param emitOnEmpty Whether the callback that is returned will emit with an empty value.
         * @returns The callback to be executed when it is necessary to emit the event.
         */
        makeEventEmitter(emitOnEmpty = false) {
            return (selectedDates) => {
                List.head(selectedDates)
                    .map(d => moment.utc(d).local())
                    .filter(m => m.isValid())
                    .caseOf({
                    Nothing: () => {
                        if (emitOnEmpty) {
                            tsx.emitOn(this, 'onInput', Maybe.empty());
                        }
                    },
                    Just: selectedMoment => {
                        const toEmit = Maybe.of(expr.switcher(this.secondsBehavior, {
                            startOfMinute: () => selectedMoment.startOf('minute'),
                            endOfMinute: () => selectedMoment.endOf('minute'),
                        }));
                        tsx.emitOn(this, 'onInput', toEmit);
                    },
                });
            };
        },
    },
    /**
     * Renders the main body of the component.
     *
     * @returns The node containing the component body.
     */
    render() {
        return (<b-input-group v-b-popover_top_hover_focus={this.popover} scopedSlots={this.getBodyScopedSlots()}>
                {this.renderBodyNode()}
                {ifJustOrEmpty(Maybe.fromNullable(this.reset), this.renderResetButton)}
            </b-input-group>);
    },
});
